import { Mail, MessageSquareCheck, PhoneCall } from "lucide-react";
import CTASection from "../components/sections/common/CTASection";
import ContactForm from "../components/sections/common/ContactForm";
import PageHeroSplit from "../components/sections/common/PageHeroSplit";
import TestimonialsSection from "../components/sections/contact/TestimonialsSection";
import PageMeta from "../components/ui/PageMeta";
import PrimaryButton from "../components/ui/PrimaryButton";
import Section from "../components/ui/Section";
import SectionHeading from "../components/ui/SectionHeading";
import { ctaPhone, pageCtaContent } from "../data/ctaContent";
import { contactInfo } from "../data/navigation";
import { contactPageContent, enquiryFormOptions } from "../data/contact";

const pageMeta = {
  title: "Contact TheCareerSpace | Counselling & Admissions Enquiry",
  description:
    "Connect with TheCareerSpace for counselling on bachelor's, master's, MBBS in India, and MBBS abroad admissions pathways."
};

const contactMethodIcons = {
  "Call the Counselling Desk": PhoneCall,
  "Write to Us": Mail,
  "Enquiry Through Form": MessageSquareCheck
};

const CONTACT_HERO_IMAGE =
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=80";

function Contact() {
  return (
    <>
      <PageMeta title={pageMeta.title} description={pageMeta.description} />

      <PageHeroSplit
        badge={contactPageContent.hero.badge}
        title={contactPageContent.hero.title}
        description={contactPageContent.hero.description}
        highlights={[
          contactPageContent.hero.trustNote,
          "Student + parent-inclusive conversations",
          "Support across all admission pathways"
        ]}
        primaryCta={{ label: "Call Counselling Desk", href: `tel:${contactInfo.phoneHref}` }}
        secondaryCta={{ label: "Go to Enquiry Form", href: "#enquiry-form" }}
        sideTitle="How you can reach us"
        sideDescription="Choose the contact mode that is most convenient for your family."
        sideItems={contactPageContent.methods.map((method) => method.title)}
        heroImage={CONTACT_HERO_IMAGE}
      />

      <Section className="relative overflow-hidden bg-white">
        <div className="pointer-events-none absolute inset-0 opacity-55">
          <div
            className="h-full w-full"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(116,119,127,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(116,119,127,0.08) 1px, transparent 1px)",
              backgroundSize: "60px 60px"
            }}
          />
        </div>

        <div className="relative">
          <SectionHeading
            badge="Contact Options"
            title="Reach TheCareerSpace through your preferred counselling channel"
            description="Whether you prefer call, email, or form-based enquiry, our team is available to guide your next step."
          />

          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {contactPageContent.methods.map((method) => {
              const Icon = contactMethodIcons[method.title] || MessageSquareCheck;

              return (
                <article key={method.title} className="border-t-2 border-brand-gold-strong bg-white p-6 shadow-card">
                  <div className="inline-flex h-10 w-10 items-center justify-center bg-brand-sky/55 text-brand-navy">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h2 className="mt-4 text-xl font-semibold text-brand-navy">{method.title}</h2>
                  <p className="mt-2 text-sm font-medium text-brand-charcoal/84">{method.detail}</p>
                  <p className="mt-2 text-sm leading-relaxed text-brand-charcoal/72">{method.helper}</p>
                  <PrimaryButton
                    className="mt-5 w-full bg-brand-gold text-brand-navy hover:bg-brand-gold-strong"
                    href={method.actionHref}
                  >
                    {method.actionLabel}
                  </PrimaryButton>
                </article>
              );
            })}
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <article className="border border-brand-line bg-brand-sky/35 p-5 shadow-card">
              <p className="text-xs font-semibold uppercase tracking-[0.13em] text-brand-charcoal/66">For Students</p>
              <p className="mt-2 text-sm leading-relaxed text-brand-charcoal/84">
                {contactPageContent.audienceReassurance.students}
              </p>
            </article>
            <article className="border border-brand-line bg-white p-5 shadow-card">
              <p className="text-xs font-semibold uppercase tracking-[0.13em] text-brand-charcoal/66">For Parents</p>
              <p className="mt-2 text-sm leading-relaxed text-brand-charcoal/84">
                {contactPageContent.audienceReassurance.parents}
              </p>
            </article>
          </div>
        </div>
      </Section>

      <TestimonialsSection />

      <Section className="relative overflow-hidden bg-gradient-to-b from-brand-sky/30 via-white to-brand-sky/25">
        <div className="pointer-events-none absolute inset-0 opacity-45">
          <div
            className="h-full w-full"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(116,119,127,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(116,119,127,0.08) 1px, transparent 1px)",
              backgroundSize: "60px 60px"
            }}
          />
        </div>

        <div className="relative grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
          <div className="max-w-xl pt-2 lg:pt-6">
            <p className="inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-gold-strong">
              <span className="h-px w-10 bg-brand-gold-strong/70" />
              Counselling-first Enquiry
            </p>
            <h2 className="mt-6 text-3xl font-semibold leading-tight text-brand-navy sm:text-5xl">
              Begin with a calm conversation,
              <span className="block italic text-brand-navy/78">then move step by step.</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-brand-charcoal/82 sm:text-lg">
              {contactPageContent.consultationIntro} Our team keeps the discussion practical, transparent, and aligned to
              student profile fit plus family priorities.
            </p>
            <p className="mt-4 text-base leading-relaxed text-brand-charcoal/78">
              Use the form to share your current stage and we will guide the next steps in a clear sequence.
            </p>
            <p className="mt-7 text-sm font-semibold uppercase tracking-[0.12em] text-brand-charcoal/68">
              Counselling Desk:{" "}
              <a className="text-brand-navy hover:text-brand-gold-strong" href={`tel:${contactInfo.phoneHref}`}>
                {contactInfo.phoneDisplay}
              </a>
              {" · "}
              <a className="text-brand-navy hover:text-brand-gold-strong" href={`mailto:${contactInfo.email}`}>
                {contactInfo.email}
              </a>
            </p>
          </div>

          <ContactForm
            enquirerRoles={enquiryFormOptions.enquirerRoles}
            interestAreas={enquiryFormOptions.interestAreas}
            preferredStudyPaths={enquiryFormOptions.preferredStudyPaths}
            counsellingModes={enquiryFormOptions.counsellingModes}
          />
        </div>
      </Section>

      <CTASection
        {...pageCtaContent.contact}
        phone={ctaPhone}
        secondaryCtaClassName="border-brand-gold/55 bg-brand-gold-soft text-brand-navy hover:border-brand-gold hover:bg-brand-gold hover:text-brand-navy"
      />
    </>
  );
}

export default Contact;
